import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Menu, X, ChevronDown } from 'lucide-react';
import logo from '../assets/icon.ico';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'About US', path: '/about' },
  ];

  const serviceItems = [
    { name: 'Cyber Security & Intelligence', path: '/services/cyber-security' },
    { name: 'Counter-Intelligence & Surveillance', path: '/services/counter-intelligence' },
    { name: 'Surveillance & Monitoring Systems', path: '/services/surveillance' },
    { name: 'Drone Operations & Services', path: '/services/drone-operations' },
    { name: 'Digital Forensics & Recovery', path: '/services/digital-forensics' },
    { name: 'Software Development', path: '/services/software-development' },
    { name: 'Fire Fighting System', path: '/services/fire-fighting-system' },
    { name: 'Warehouse Management', path: '/services/warehouse-management' },
    { name: 'Research & Development', path: '/services/research-development' },
    { name: 'Safety Equipment', path: '/services/safety-equipment' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== '/'
          ? 'glass-morphism border-b border-royal-gold/20'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 rounded-12 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <img src={logo} alt="CIARL Logo" className="w-12 h-12" />
              {/* <Shield className="w-6 h-6 text-near-black" /> */}
            </div>
            <div>
              <div className="heading-font text-2xl font-bold text-royal-gold">CIARL</div>
              <div className="text-xs text-gray-400">Cyber Intelligence & Research Lab</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsServicesDropdownOpen(true)}
                    onMouseLeave={() => setIsServicesDropdownOpen(false)}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-1 text-sm font-medium transition-colors duration-300 hover:text-royal-gold ${
                        location.pathname === item.path || location.pathname.startsWith('/services')
                          ? 'text-royal-gold'
                          : 'text-gray-300'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Link>
                    
                    {/* Services Dropdown */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ 
                        opacity: isServicesDropdownOpen ? 1 : 0,
                        y: isServicesDropdownOpen ? 0 : 10,
                        display: isServicesDropdownOpen ? 'block' : 'none'
                      }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-80 bg-gray-900/95 rounded-16 border border-gray-900 shadow-lg"
                    >
                      <div className="p-4 space-y-2">
                        {serviceItems.map((service) => (
                          <Link
                            key={service.name}
                            to={service.path}
                            className="block px-4 py-3 text-sm text-gray-300 hover:text-royal-gold hover:bg-royal-gold/10 rounded-12 transition-all duration-300"
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`text-sm font-medium transition-colors duration-300 hover:text-royal-gold ${
                      location.pathname === item.path
                        ? 'text-royal-gold'
                        : 'text-gray-300'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* Contact Button */}
            <Link to="/contact" className="cta-button">
              Contact US
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors duration-300 ${isMobileMenuOpen ? 'text-white' : 'text-gray-300'} hover:text-royal-gold ${isMobileMenuOpen ? 'fixed top-6 right-6 z-99' : ''}`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden transition-opacity duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ 
            opacity: isMobileMenuOpen ? 1 : 0,
            height: isMobileMenuOpen ? 'auto' : 0
          }}
          transition={{ duration: 0.3 }}
          className="lg:hidden overflow-hidden z-50"
          style={{ position: isMobileMenuOpen ? 'fixed' : 'static', top: 0, left: 0, right: 0, background: isMobileMenuOpen ? 'rgba(20,20,20,0.95)' : 'none', backdropFilter: isMobileMenuOpen ? 'blur(8px)' : 'none' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="py-4 space-y-4 border-t border-gray-700">
            {navItems.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 text-base font-medium transition-colors duration-300 hover:text-royal-gold ml-4 ${
                    location.pathname === item.path
                      ? 'text-royal-gold'
                      : 'text-gray-300'
                  }`}
                >
                  {item.name}
                </Link>
                {item.hasDropdown && (
                  <div className="ml-8 mt-2 space-y-2">
                    {serviceItems.map((service) => (
                      <Link
                        key={service.name}
                        to={service.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-1 text-sm text-gray-400 hover:text-royal-gold transition-colors duration-300 ml-2"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="inline-block cta-button mt-4 ml-4 text-base"
            >
              Contact US
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navigation;